const MARKUP = /<\s*\/?\s*(b|strong|i|em|u|br|div|p)\b[^>]*>/i;

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function decodeHtml(text) {
  return String(text)
    .replace(/&nbsp;/gi, " ")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&amp;/gi, "&");
}

function sameStyle(a, b) {
  return a.bold === b.bold && a.italic === b.italic && a.underline === b.underline;
}

/** Paragraphs of styled runs. Plain text stays plain, including blank lines. */
export function parseNoteBlocks(value) {
  const raw = String(value || "");
  if (!raw) return [];
  if (!MARKUP.test(raw)) {
    return raw.split("\n").map((line) => (
      line ? [{ text: line, bold: false, italic: false, underline: false }] : []
    ));
  }
  const blocks = [[]];
  const style = { bold: 0, italic: 0, underline: 0 };
  const re = /<\s*(\/)?\s*(b|strong|i|em|u|br|div|p)\b[^>]*>|[^<]+|<[^<]*/gi;
  const push = (text) => {
    const decoded = decodeHtml(text);
    if (!decoded) return;
    const run = {
      text: decoded,
      bold: style.bold > 0,
      italic: style.italic > 0,
      underline: style.underline > 0,
    };
    const block = blocks[blocks.length - 1];
    const prev = block[block.length - 1];
    if (prev && sameStyle(prev, run)) prev.text += run.text;
    else block.push(run);
  };
  const breakLine = () => {
    if (blocks[blocks.length - 1].length) blocks.push([]);
  };
  let keepTrailingBreak = false;
  let match = re.exec(raw);
  while (match) {
    const token = match[0];
    if (token[0] !== "<" || !match[2]) {
      push(token);
      if (token.trim()) keepTrailingBreak = false;
    } else {
      const closing = Boolean(match[1]);
      const tag = match[2].toLowerCase();
      if (tag === "br") {
        blocks.push([]);
        keepTrailingBreak = true;
      } else if (tag === "div" || tag === "p") {
        if (!closing) breakLine();
        else if (blocks[blocks.length - 1].length) blocks.push([]);
        keepTrailingBreak = false;
      } else {
        const key = tag === "strong" || tag === "b" ? "bold" : tag === "em" || tag === "i" ? "italic" : "underline";
        style[key] += closing ? -1 : 1;
        if (style[key] < 0) style[key] = 0;
        keepTrailingBreak = false;
      }
    }
    match = re.exec(raw);
  }
  if (!keepTrailingBreak) {
    while (blocks.length > 1 && !blocks[blocks.length - 1].length) blocks.pop();
  }
  return blocks;
}

function serializeBlocks(blocks) {
  const cleaned = blocks.slice();
  const hasText = cleaned.some((block) => block.some((run) => run.text));
  if (!hasText && cleaned.length < 2) return "";
  const styled = cleaned.some((block) => block.some((run) => run.bold || run.italic || run.underline));
  if (!styled) {
    return cleaned.map((block) => block.map((run) => run.text).join("")).join("\n");
  }
  return cleaned.map((block) => block.map((run) => {
    let out = escapeHtml(run.text);
    if (run.underline) out = `<u>${out}</u>`;
    if (run.italic) out = `<i>${out}</i>`;
    if (run.bold) out = `<b>${out}</b>`;
    return out;
  }).join("")).join("<br>");
}

export function noteHtmlForEditor(value) {
  const serialized = serializeBlocks(parseNoteBlocks(value));
  if (!serialized) return "";
  const html = MARKUP.test(serialized)
    ? serialized
    : escapeHtml(serialized).replace(/\n/g, "<br>");
  // A final break has no line box unless something follows it.
  if (serialized.endsWith("\n") || /<br>$/i.test(serialized)) return `${html}\u200b`;
  return html;
}

function styleFromElement(el, style) {
  const tag = el.tagName;
  const css = el.style || {};
  const decoration = `${css.textDecoration || ""} ${css.textDecorationLine || ""}`;
  const weight = css.fontWeight;
  return {
    bold: style.bold || tag === "B" || tag === "STRONG" || weight === "bold" || Number(weight) >= 600,
    italic: style.italic || tag === "I" || tag === "EM" || css.fontStyle === "italic",
    underline: style.underline || tag === "U" || decoration.includes("underline"),
  };
}

/** Read a contenteditable text box back into stored note text. */
export function readNoteField(el, maxLength, singleLine) {
  const blocks = [[]];
  let count = 0;
  const pushText = (text, style) => {
    if (!text || count >= maxLength) return;
    const slice = text.slice(0, maxLength - count);
    count += slice.length;
    const block = blocks[blocks.length - 1];
    const prev = block[block.length - 1];
    if (prev && sameStyle(prev, style)) prev.text += slice;
    else block.push({ text: slice, bold: style.bold, italic: style.italic, underline: style.underline });
  };
  const pushBreak = () => {
    if (singleLine || count >= maxLength) return;
    blocks.push([]);
  };
  const walk = (node, style) => {
    if (count >= maxLength) return;
    if (node.nodeType === 3) {
      let text = String(node.nodeValue || "").replace(/\u00a0/g, " ").replace(/\u200b/g, "");
      if (singleLine) text = text.replace(/[\r\n]+/g, "");
      pushText(text, style);
      return;
    }
    if (node.nodeType !== 1) return;
    const tag = node.tagName;
    if (tag === "BR") {
      pushBreak();
      return;
    }
    const next = styleFromElement(node, style);
    if ((tag === "DIV" || tag === "P") && node !== el && blocks[blocks.length - 1].length) pushBreak();
    node.childNodes.forEach((child) => walk(child, next));
  };
  walk(el, { bold: false, italic: false, underline: false });
  return serializeBlocks(blocks);
}
